/**
 * Manual scroll restoration on reload / bfcache (all templates).
 *
 * Head inline in theme.liquid decides whether this load is a *return* to the page
 * (refresh, browser back/forward, or coming back from a product page opened here)
 * and only then raises the pending flag. Arriving fresh starts at the top.
 * Position is stored per URL: theme:scroll-y:{pathname}{search}
 */
class ScrollRestore {
  static NEAR_BOTTOM_THRESHOLD = 48;
  static RETURN_KEY = 'theme:scroll-return-to';
  /* Read once, before clearPendingRestore() removes the attribute. */
  static #enabled =
    document.documentElement.getAttribute('data-scroll-restore-pending') === '1';
  static #persistRafId = null;
  static #listenersBound = false;
  static #layoutObserver = null;
  static #pendingClearTimeout = null;
  static #restoreComplete = false;
  static #layoutSettleTimeout = null;

  static #routeSuffix() {
    return `${location.pathname}${location.search}`;
  }

  static #scrollKey() {
    return `theme:scroll-y:${ScrollRestore.#routeSuffix()}`;
  }

  static #nearBottomKey() {
    return `theme:scroll-near-bottom:${ScrollRestore.#routeSuffix()}`;
  }

  static #maxScrollY() {
    return Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
  }

  static persistScrollPosition() {
    if (
      !ScrollRestore.#restoreComplete &&
      document.documentElement.getAttribute('data-scroll-restore-pending') === '1'
    ) {
      return;
    }

    const y = window.scrollY || document.documentElement.scrollTop || 0;
    const maxY = ScrollRestore.#maxScrollY();
    const nearBottom = maxY > 0 && y >= maxY - ScrollRestore.NEAR_BOTTOM_THRESHOLD;

    try {
      sessionStorage.setItem(ScrollRestore.#scrollKey(), String(y));
      sessionStorage.setItem(ScrollRestore.#nearBottomKey(), nearBottom ? '1' : '0');
    } catch {
      /* private mode / quota */
    }
  }

  static readSavedScrollY() {
    try {
      const raw = sessionStorage.getItem(ScrollRestore.#scrollKey());
      const y = raw ? parseInt(raw, 10) : 0;
      return Number.isFinite(y) && y > 0 ? y : 0;
    } catch {
      return 0;
    }
  }

  static #wasNearBottom() {
    try {
      return sessionStorage.getItem(ScrollRestore.#nearBottomKey()) === '1';
    } catch {
      return false;
    }
  }

  static #computeTargetScrollY() {
    const savedY = ScrollRestore.readSavedScrollY();
    if (savedY < 1) return 0;

    const currentMaxY = ScrollRestore.#maxScrollY();
    if (ScrollRestore.#wasNearBottom()) {
      return currentMaxY;
    }

    return Math.min(savedY, currentMaxY);
  }

  static #applyScroll(target) {
    const root = document.documentElement;
    root.style.removeProperty('overflow');

    window.scrollTo({ top: target, left: 0, behavior: 'auto' });
    root.scrollTop = target;
    document.body.scrollTop = target;

    HeaderHeightsSync.sync();
    HeaderHeightsSync.applyHeaderStateForScrollY(target);
  }

  static #isSettled(target) {
    const current = window.scrollY || document.documentElement.scrollTop || 0;
    return target < 1 || Math.abs(current - target) < 2;
  }

  static clearPendingRestore() {
    document.documentElement.removeAttribute('data-scroll-restore-pending');
    document.documentElement.style.removeProperty('overflow');
    document.documentElement.style.removeProperty('scroll-behavior');
    ScrollRestore.#layoutObserver?.disconnect();
    ScrollRestore.#layoutObserver = null;
    if (ScrollRestore.#pendingClearTimeout !== null) {
      clearTimeout(ScrollRestore.#pendingClearTimeout);
      ScrollRestore.#pendingClearTimeout = null;
    }
    if (ScrollRestore.#layoutSettleTimeout !== null) {
      clearTimeout(ScrollRestore.#layoutSettleTimeout);
      ScrollRestore.#layoutSettleTimeout = null;
    }
    ScrollRestore.#restoreComplete = true;
  }

  static restore() {
    if (!ScrollRestore.#enabled) return true;

    const target = ScrollRestore.#computeTargetScrollY();
    if (target < 1) {
      ScrollRestore.clearPendingRestore();
      return true;
    }

    ScrollRestore.#applyScroll(target);
    return ScrollRestore.#isSettled(target);
  }

  static #finalizeRestore({ force = false } = {}) {
    ScrollRestore.restore();
    const target = ScrollRestore.#computeTargetScrollY();
    if (ScrollRestore.#isSettled(target) || force) {
      ScrollRestore.clearPendingRestore();
    }
  }

  static #watchLayoutUntilRestored() {
    if (ScrollRestore.#layoutObserver || !('ResizeObserver' in window)) return;
    if (document.documentElement.getAttribute('data-scroll-restore-pending') !== '1') return;

    ScrollRestore.#layoutObserver = new ResizeObserver(() => {
      if (ScrollRestore.#restoreComplete) return;

      const current = window.scrollY || document.documentElement.scrollTop || 0;
      if (current > 2) return;

      requestAnimationFrame(() => {
        ScrollRestore.restore();
      });
    });
    ScrollRestore.#layoutObserver.observe(document.body);

    ScrollRestore.#layoutSettleTimeout = setTimeout(() => {
      ScrollRestore.#finalizeRestore({ force: true });
    }, 2500);
  }

  static #schedulePendingFallbackClear() {
    if (ScrollRestore.#pendingClearTimeout !== null) return;
    ScrollRestore.#pendingClearTimeout = setTimeout(() => {
      ScrollRestore.#pendingClearTimeout = null;
      if (document.documentElement.getAttribute('data-scroll-restore-pending') === '1') {
        ScrollRestore.#finalizeRestore({ force: true });
      }
    }, 5000);
  }

  static #bindListeners() {
    if (ScrollRestore.#listenersBound) return;
    ScrollRestore.#listenersBound = true;

    /* Leaving for a product page is the one forward navigation that earns a scroll
       restore on the way back, so mark the route we are leaving. Product -> product
       keeps the original marker, otherwise a detour through a recommended product
       would lose the collection we came from. */
    document.addEventListener(
      'click',
      (event) => {
        if (location.pathname.includes('/products/')) return;
        if (!event.target.closest?.('a[href*="/products/"]')) return;

        try {
          sessionStorage.setItem(ScrollRestore.RETURN_KEY, ScrollRestore.#routeSuffix());
        } catch {
          /* private mode / quota */
        }
      },
      { capture: true }
    );

    window.addEventListener('pagehide', ScrollRestore.persistScrollPosition, { capture: true });
    window.addEventListener('beforeunload', ScrollRestore.persistScrollPosition, { capture: true });

    window.addEventListener(
      'scroll',
      () => {
        if (ScrollRestore.#persistRafId !== null) return;
        ScrollRestore.#persistRafId = requestAnimationFrame(() => {
          ScrollRestore.#persistRafId = null;
          ScrollRestore.persistScrollPosition();
        });
      },
      { passive: true, capture: true }
    );

    window.addEventListener('load', () => {
      ScrollRestore.#finalizeRestore();
      requestAnimationFrame(() => {
        ScrollRestore.restore();
        requestAnimationFrame(() => {
          ScrollRestore.#finalizeRestore({ force: true });
        });
      });
    });
  }

  static init() {
    ScrollRestore.#bindListeners();
    ScrollRestore.restore();
    ScrollRestore.#watchLayoutUntilRestored();
    ScrollRestore.#schedulePendingFallbackClear();
    window.themeRestoreScrollPosition = () => ScrollRestore.restore();
  }
}

ScrollRestore.init();
